import { LogOut, Menu, Moon, Package, Search, Settings, Sun } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, Outlet, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { logoutRequest, meRequest } from '@/api/auth'
import type { AdminMenuNode } from '@/api/menu'
import { fetchAdminMenu } from '@/api/menu'
import { AdminBrandLogo, brandAssetsFromBranding } from '@/components/brand/AdminBrandLogo'
import { AdminBreadcrumbs } from '@/components/layout/AdminBreadcrumbs'
import { AdminLanguageSwitcher } from '@/components/layout/AdminLanguageSwitcher'
import { AdminNotificationBells } from '@/components/layout/AdminNotificationBells'
import { AdminSidebarNav } from '@/components/layout/AdminSidebarNav'
import { CommandPalette } from '@/components/layout/CommandPalette'
import { PlatformModulesNotice } from '@/components/PlatformModulesNotice'
import { useAdminTheme, useSidebarCollapsed } from '@/hooks/useAdminLayoutPreferences'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useEnabledModuleKeys, useModules } from '@/hooks/useModule'
import { tenantDisplayName, useTenantBranding } from '@/hooks/useTenantBranding'
import { useT } from '@/lib/i18n/AdminI18nProvider'
import { platformHorizontalLogo, resolveAdminAvatarSrc } from '@/lib/platform-brand'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/auth.store'

function userInitials(username: string | undefined, email: string | undefined) {
  const u = (username?.trim() || email?.trim() || '').slice(0, 2)
  return u ? u.toUpperCase() : '?'
}

function UserAvatarMark({
  src,
  initials,
}: {
  src: string | null
  initials: string
}) {
  const [broken, setBroken] = useState(false)

  useEffect(() => {
    setBroken(false)
  }, [src])

  const showImg = Boolean(src) && !broken

  if (showImg && src) {
    return (
      <img
        src={src}
        alt=""
        className="size-9 rounded-full object-cover shadow-glow ring-1 ring-border/60"
        onError={() => setBroken(true)}
      />
    )
  }

  return (
    <span className="grid size-9 place-content-center rounded-full bg-gradient-to-br from-mainColor/90 to-darkMainColor text-xs font-bold text-white shadow-glow">
      {initials}
    </span>
  )
}

export function AppLayout() {
  return <AppLayoutInner />
}

function AppLayoutInner() {
  const t = useT()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const setUser = useAuthStore((s) => s.setUser)
  const { isLoading: modulesLoading } = useModules()
  const enabledModules = useEnabledModuleKeys()
  const [commandOpen, setCommandOpen] = useState(false)
  const { data: menuTree, isLoading: menuLoading, isError: menuError } = useQuery({
    queryKey: ['admin-menu'],
    queryFn: fetchAdminMenu,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
  const { mode, toggle: toggleTheme } = useAdminTheme()
  const { collapsed, toggle: toggleSidebar } = useSidebarCollapsed()
  const { data: branding } = useTenantBranding()
  const storeName = tenantDisplayName(branding)
  const brandAssets = branding ? brandAssetsFromBranding(branding) : {}
  const avatarSrc = resolveAdminAvatarSrc(user)

  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)

  const userMenuRef = useRef<HTMLDivElement>(null)

  const fallbackMenu = useMemo<AdminMenuNode[]>(
    () => [
      {
        id: -1,
        name: t('nav.section.sales'),
        icon: null,
        path: null,
        module_key: null,
        children: [
          {
            id: -2,
            name: t('nav.home'),
            icon: 'fas fa-tachometer-alt',
            path: '/',
            module_key: null,
            children: [],
          },
        ],
      },
    ],
    [t],
  )

  useEffect(() => {
    if (!user) return
    let cancelled = false
    void meRequest()
      .then((res) => {
        if (cancelled || !res.success || !res.data) return
        const info = res.data.information
        setUser({
          id: res.data.id,
          username: res.data.username,
          email: res.data.email,
          user_type: res.data.user_type,
          account_status: res.data.account_status,
          roles: res.data.roles ?? user.roles,
          avatar: res.data.avatar ?? info?.profile_picture ?? null,
          information: info
            ? {
                name: info.name ?? null,
                surname: info.surname ?? null,
                phone: info.phone ?? null,
                profile_picture: info.profile_picture ?? null,
              }
            : null,
        })
      })
      .catch(() => {
        /* sessiz — mevcut store yeter */
      })
    return () => {
      cancelled = true
    }
    // Sadece mount / oturum değişiminde
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  useEffect(() => {
    if (!userMenuOpen) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (userMenuOpen && !userMenuRef.current?.contains(target)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [userMenuOpen])

  async function handleLogout() {
    try {
      await logoutRequest()
    } catch {
      /* token zaten geçersiz olabilir */
    }
    sessionStorage.removeItem('platform_access_active')
    logout()
    toast.success(t('shell.logoutDone'))
    navigate('/login', { replace: true })
  }

  const platformAccess =
    typeof window !== 'undefined' && sessionStorage.getItem('platform_access_active') === '1'

  const menuForNav = menuError || !menuTree?.length ? fallbackMenu : menuTree

  const isLgUp = useMediaQuery('(min-width: 1024px)')
  const sidebarCollapsed = isLgUp && collapsed
  const sidebarWidth = sidebarCollapsed ? 72 : 260
  const mainMarginLeft = isLgUp ? sidebarWidth : 0

  useEffect(() => {
    if (isLgUp) setMobileNavOpen(false)
  }, [isLgUp])

  return (
    <div className="flex min-h-screen bg-shell text-foreground">
      {platformAccess ? (
        <div className="fixed inset-x-0 top-0 z-[60] bg-amber-600 px-4 py-2 text-center text-sm font-medium text-white">
          {t('shell.platformBanner')}{' '}
          <button type="button" className="underline underline-offset-2" onClick={() => void handleLogout()}>
            {t('shell.closeSession')}
          </button>
        </div>
      ) : null}

      {mobileNavOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          aria-label={t('shell.closeMenu')}
          onClick={() => setMobileNavOpen(false)}
        />
      ) : null}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col border-r border-border/80 bg-card shadow-soft transition-[width,transform] duration-300 ease-out dark:border-gray-700/80 dark:bg-secondaryDark',
          platformAccess && 'top-10',
          sidebarCollapsed ? 'w-[72px]' : 'w-[260px]',
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <div
          className={cn(
            'flex h-16 shrink-0 items-center gap-2 border-b border-border/80 px-3 dark:border-gray-700/80',
            sidebarCollapsed && 'justify-center px-2',
          )}
        >
          <AdminBrandLogo
            {...brandAssets}
            storeName={storeName}
            variant={sidebarCollapsed ? 'icon' : 'logo'}
            isDark={mode === 'dark'}
            className={sidebarCollapsed ? 'justify-center' : ''}
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
          {menuLoading || modulesLoading ? (
            <p className="px-2 text-xs text-muted-foreground">{t('shell.menuLoading')}</p>
          ) : (
            <AdminSidebarNav
              menu={menuForNav}
              enabledModuleKeys={enabledModules}
              modulesLoading={modulesLoading}
              collapsed={sidebarCollapsed}
              isDesktopLayout={isLgUp}
              onNavigate={() => setMobileNavOpen(false)}
            />
          )}
        </div>
      </aside>

      <div
        className="flex min-h-svh min-w-0 flex-1 flex-col transition-[margin] duration-300 ease-out"
        style={{ marginLeft: mainMarginLeft }}
      >
        <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center justify-between gap-3 border-b border-border/80 bg-card/90 px-3 shadow-soft backdrop-blur-md dark:border-gray-700/80 dark:bg-secondaryDark/90 md:px-5">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <button
              type="button"
              className="inline-flex rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground lg:hidden"
              aria-label={t('shell.openMenu')}
              onClick={() => setMobileNavOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </button>
            <button
              type="button"
              className="hidden rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground lg:inline-flex"
              aria-label={t('shell.sidebar')}
              onClick={toggleSidebar}
            >
              <Menu className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => setCommandOpen(true)}
              className="relative mx-1 hidden max-w-xs flex-1 md:block md:max-w-md"
            >
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <span className="flex h-10 w-full items-center rounded-full border border-input bg-background/80 pl-10 pr-16 text-sm text-muted-foreground shadow-inner transition-colors hover:border-mainColor/40 hover:bg-muted/30">
                {t('shell.searchPlaceholder')}
              </span>
              <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-border bg-muted/60 px-1.5 py-0.5 text-[10px] text-muted-foreground sm:inline">
                Ctrl+K
              </kbd>
            </button>
          </div>

          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <AdminNotificationBells />
            <AdminLanguageSwitcher />

            <button
              type="button"
              onClick={toggleTheme}
              className="rounded-xl p-2.5 text-muted-foreground transition-all hover:bg-muted hover:text-foreground"
              aria-label={mode === 'dark' ? t('shell.lightTheme') : t('shell.darkTheme')}
            >
              {mode === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>

            <div className="relative pl-1" ref={userMenuRef}>
              <button
                type="button"
                onClick={() => setUserMenuOpen((o) => !o)}
                className="flex items-center gap-2 rounded-xl py-1 pl-1 pr-2 transition-colors hover:bg-muted"
                aria-expanded={userMenuOpen}
                aria-haspopup="true"
              >
                <UserAvatarMark
                  src={avatarSrc}
                  initials={userInitials(user?.username, user?.email)}
                />
                <span className="hidden max-w-[140px] truncate text-left text-sm font-medium lg:block">
                  {user?.username ?? user?.email ?? t('common.account')}
                </span>
              </button>
              {userMenuOpen ? (
                <div className="absolute right-0 mt-2 w-56 origin-top-right animate-in fade-in zoom-in-95 slide-in-from-top-2 rounded-2xl border border-border bg-card py-2 shadow-strong duration-200 dark:border-gray-700 dark:bg-secondaryDark">
                  <div className="border-b border-border px-3 pb-2 dark:border-gray-700">
                    <p className="truncate text-sm font-medium">{user?.username ?? t('common.account')}</p>
                    <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <div className="py-1">
                    <Link
                      to="/panel-settings"
                      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Settings className="h-4 w-4" />
                      {t('common.settings')}
                    </Link>
                    <Link
                      to="/modules"
                      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Package className="h-4 w-4" />
                      {t('shell.planModules')}
                    </Link>
                  </div>
                  <div className="border-t border-border pt-1 dark:border-gray-700">
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-muted"
                      onClick={() => void handleLogout()}
                    >
                      <LogOut className="h-4 w-4" />
                      {t('shell.logoutFull')}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <main className="min-w-0 flex-1 px-3 py-4 md:px-5">
          <AdminBreadcrumbs className="mb-3" />
          <PlatformModulesNotice />
          <Outlet />
        </main>

        <footer className="mt-auto shrink-0 border-t border-border/70 bg-card/60 px-3 py-3 dark:border-gray-700/70 dark:bg-secondaryDark/50 md:px-5">
          <div className="flex flex-col items-center justify-between gap-2 sm:flex-row sm:items-center">
            <p className="text-center text-[11px] leading-relaxed text-muted-foreground sm:text-left">
              {t('shell.footerBuiltWith')}
            </p>
            <a
              href="https://codexfix.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 opacity-80 transition-opacity hover:opacity-100"
              title="CodexFix"
            >
              <img
                src={platformHorizontalLogo(mode === 'dark')}
                alt="CodexFix"
                className="h-5 w-auto max-w-[140px] object-contain"
              />
            </a>
          </div>
        </footer>
      </div>

      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
    </div>
  )
}
